import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES, TONE } from '../../data/constants'
import Chip from '../Chip/Chip'
import { useAIRiskReportExportMode } from '../RenderMode/AIRiskReportRenderModeProvider'
import ScoreProgress from '../ScoreProgress/ScoreProgress'
import StatGroup from '../StatGroup/StatGroup'
import styles from './SubBlock.module.scss'

export default function SubBlock({
  titleId,
  subtitleId = undefined,
  score = null,
  tone = TONE.NEUTRAL,
  stats = null,
  mocked = false,
  hideFromExport = false,
  defaultCollapsed = true,
  children = null,
}) {
  const [isOpen, setIsOpen] = useState(!defaultCollapsed)
  const isExportMode = useAIRiskReportExportMode()

  if (isExportMode && hideFromExport) return null

  const open = isExportMode || isOpen
  const disabled = mocked || !Number.isFinite(score)
  const headerClassName = [
    styles.header,
    isExportMode ? styles.headerStatic : '',
    'w-100 d-flex align-items-start justify-content-between gap-3 p-0 border-0 text-start bg-transparent',
  ]
    .filter(Boolean)
    .join(' ')
  const headerContent = (
    <>
      <span className={`${styles.titleBlock} d-flex flex-column gap-0`}>
        <span className="d-flex align-items-center gap-2 flex-wrap">
          <span className={styles.title}>
            <FormattedMessage id={titleId} />
          </span>
          {mocked && (
            <Chip tone={TONE.INFO}>
              <FormattedMessage id="AI_RISK_REPORT.COMING_SOON_OVERLAY.TITLE" />
            </Chip>
          )}
        </span>
        {subtitleId && (
          <span className={styles.subtitle}>
            <FormattedMessage id={subtitleId} />
          </span>
        )}
      </span>
      {!isExportMode && (
        <span className={`${styles.toggle} d-inline-flex align-items-center gap-2 flex-shrink-0`}>
          <FormattedMessage id={open ? 'AI_RISK_REPORT.HIDE_DETAILS' : 'AI_RISK_REPORT.VIEW_DETAILS'} />
          <i className={`fas fa-chevron-${open ? 'down' : 'right'} ${styles.toggleIcon}`} aria-hidden="true" />
        </span>
      )}
    </>
  )

  return (
    <section className={styles.block} {...AI_RISK_REPORT_PDF_CHUNK_ATTRIBUTES}>
      {isExportMode ? (
        <div className={headerClassName}>{headerContent}</div>
      ) : (
        <button
          type="button"
          className={headerClassName}
          aria-expanded={open}
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
        >
          {headerContent}
        </button>
      )}

      <div className={styles.progressWrap}>
        <ScoreProgress value={score} tone={tone} disabled={disabled} />
      </div>
      {stats && <StatGroup items={stats} />}
      {open && children && <div className={`${styles.body} ${stats ? styles.bodyAfterStats : ''}`}>{children}</div>}
    </section>
  )
}

SubBlock.propTypes = {
  titleId: PropTypes.string.isRequired,
  subtitleId: PropTypes.string,
  score: PropTypes.number,
  tone: PropTypes.oneOf(Object.values(TONE)),
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
      denom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      tone: PropTypes.oneOf(Object.values(TONE)),
      label: PropTypes.node.isRequired,
    }),
  ),
  mocked: PropTypes.bool,
  hideFromExport: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  children: PropTypes.node,
}
